import { ReusableFunctions } from './reusable-functions';
import { CategoryGroup } from '../interfaces/card-data';
import { UserAccessService } from '../services/user-access.service';
import { NavigationHelpService } from 'app/modules/shared/services/navigation-help.service';

export namespace ProductionCharts {
    export interface ChartData {
        currentDate: Date;
        yCurrent: number;
        yPredict?: number;
        previousDate?: Date;
        yPreviousMonth: number;
    }

    export interface ChartdataSeries {
        name: string;
        type?: string;
        data: any[];
        legendIndex?: number;
        index?: number;
        color?: string;
        pointPlacement?: number;
        linkedTo?: string;
        pointWidth?: number;
        grouping?: boolean;
        borderWidth?: number;
        maxPointWidth?: number;
    }

    export interface ChartDataPoint {
        x?: number | string;
        y: number;
        color?: string;
        name?: string;
        properties?: any;
        category?: number;
    }

    export interface ChartDataSeparation {
        current: Array<ChartDataPoint>;
        line: Array<ChartDataPoint>;
        prediction: Array<ChartDataPoint>;
    }

    export class ProductionCharts {
        constructor(
            private reusableFunctions: ReusableFunctions,
            private userAccessService: UserAccessService,
            private navigationHelpService: NavigationHelpService
        ) {}

        public createNewStackedChart(
            secondarytitle,
            xAxisText: string,
            yAxisText: string,
            initialSeriesData: any,
            showLegend: boolean,
            legendAlign: any,
            route: string,
            urlSegment: string,
            compareTo: string,
            lineSeriesTitle: string,
            categoryGroup: string[] | CategoryGroup[],
            purpose: string,
            loanType: string,
            isInverse = false,
            height = 330,
            spacingBottom = 0,
            showDataLabels = false,
            params = undefined,
            alternateTooltipValue: string = ''
        ): Highcharts.Options {
            const _this = this;
            let clickablePoints = this.userAccessService.hasAccessToPage(route + '/' + urlSegment);
            return {
                chart: {
                    spacingBottom: spacingBottom,
                    spacingLeft: 0,
                    marginRight: 0,
                    spacingRight: 0,
                    spacingTop: 5,
                    height: height,
                    events: {
                        load: function () {
                            if (clickablePoints) {
                                var xAxis = this.xAxis[0];
                                var xTicks = xAxis.ticks;
                                var yAxis = this.yAxis[0];
                                var points = this.series[0].points;
                                points.forEach((pointInf, i) => {
                                    if (xTicks[i]) {
                                        var label = xTicks[i].label.element;
                                        label.style.cursor = 'pointer';
                                        label.onclick = function (item) {
                                            let queryParams = {
                                                purpose: purpose,
                                                loanType: loanType,
                                                compareTo: compareTo,
                                                selectedPeriod: pointInf.properties.period,
                                                periodGroup: pointInf.properties.periodGroup,
                                            };

                                            if (urlSegment.search('fall-out') > -1) {
                                                queryParams['falloutType'] = 'All';
                                            }

                                            _this.navigationHelpService.navigateTo(
                                                [route, urlSegment],
                                                queryParams,
                                                true
                                            );
                                        };
                                    }
                                });
                            }
                        },
                    },
                },
                title: {
                    text: '',
                },
                credits: {
                    enabled: false,
                },
                series: initialSeriesData,
                exporting: {
                    enabled: false,
                    buttons: {
                        contextButton: {
                            //menuItems: Highcharts.defaultOptions.exporting.buttons.contextButton.menuItems.slice(0, 10)
                        },
                    },
                },
                xAxis: [
                    {
                        type: 'category',
                        categories: categoryGroup,
                        tickInterval: 1,
                        startOnTick: false,
                        pointIntervalUnit: xAxisText,
                        labels: {
                            enabled: true,
                            style: {
                                fontSize: '10px',
                                color: '#666666',
                            },
                        },
                        title: {
                            text: xAxisText,
                            style: {
                                fontSize: '10px',
                                color: '#666666',
                            },
                        },
                    } as Highcharts.XAxisOptions,
                ],
                yAxis: [
                    {
                        title: {
                            text: yAxisText,
                            style: {
                                fontSize: '10px',
                                color: '#666666',
                            },
                        },

                        labels: {
                            enabled: true,
                            style: {
                                fontSize: '10px',
                                color: '#666666',
                            },
                        },
                        lineWidth: 1,
                    } as Highcharts.YAxisOptions,
                ],
                legend: {
                    enabled: showLegend,
                    align: legendAlign,
                    verticalAlign: 'top',
                },
                plotOptions: {
                    column: {
                        stacking: 'normal',
                        minPointLength: 3,
                        states: {
                            hover: {
                                brightness: -0.3,
                            },
                        },
                    },
                    series: {
                        groupPadding: 0,
                        lineWidth: 3,
                        gridLineWidth: 0,
                        minorGridLineWidth: 0,
                        dataGrouping: {
                            enabled: false,
                        },
                        maxPointWidth: 50,
                        dataLabels: {
                            enabled: showDataLabels,
                            inside: false,
                            formatter: function (label) {
                                return this.point.properties.dataLabel;
                            },
                        },
                        marker: {
                            enabled: false,
                            states: {
                                hover: {
                                    radius: 4,
                                    enabled: true,
                                    radiusPlus: 0,
                                    lineWidthPlus: 0,
                                    lineWidth: 0,
                                },
                            },
                        },
                        point: {
                            events: {
                                click: function (e) {
                                    let urlSeg = urlSegment;
                                    if (urlSegment.search('fall-out') < 0) {
                                        urlSeg = urlSegment + '_by-item';
                                    }

                                    if (clickablePoints) {
                                        let queryParams = _this.getQueryParams(
                                            urlSeg,
                                            this.series.name,
                                            this.properties.period,
                                            compareTo,
                                            'T',
                                            purpose,
                                            loanType,
                                            lineSeriesTitle
                                        );

                                        if (urlSegment.search('production-by-loan-category') > -1) {
                                            urlSeg = 'production-by-loan-category';
                                            params.periodNumber = e.point.properties.period;
                                            queryParams = params;
                                        }

                                        _this.navigationHelpService.navigateTo(
                                            [route, urlSeg],
                                            queryParams,
                                            true
                                        );
                                    }
                                },
                            },
                        },
                        cursor: clickablePoints ? 'pointer' : 'default',
                    } as Highcharts.PlotSeriesOptions,
                },
                tooltip: {
                    style: {
                        color: '#223540',
                        fontFamily: 'Roboto',
                        fontSize: '12',
                        height: 12,
                    },
                    borderColor: '#223540',
                    borderRadius: 6,
                    useHTML: true,
                    shared: true,
                    formatter: function () {
                        let tooltip = '';
                        if (showLegend) {
                            let length = this.points.length;
                            let previousPeridLine = '';
                            let thisPeriodTotal = 0;
                            let thisPeriodUnitTotal = 0;
                            let comparePeriodTotal = 0;
                            let comparePeriodUnitTotal = 0;
                            let tootipTotalName = '';

                            tooltip =
                                '<table style="border-collapse: collapse">' +
                                this.points.reduce(function (s, point, index) {
                                    let toolText = '';
                                    let value = _this.reusableFunctions.formatMoney(point.y);
                                    let seriesName = point.series.name;
                                    let isPreviousPeriod =
                                        seriesName.toLowerCase() === lineSeriesTitle.toLowerCase();

                                    if (index === 0) {
                                        s += `<td colSpan="4" style="text-align: left;">
                                            <b>${point.point.properties.datePeriodDescription.toUpperCase()}<b>
                                            </tr>
                                        </td>`;
                                        thisPeriodTotal = point.total;
                                        tootipTotalName = point.point.properties.tootipTotalName;
                                    }

                                    if (!isPreviousPeriod) {
                                        thisPeriodUnitTotal += alternateTooltipValue
                                            ? point.point.properties.alternateY
                                            : point.point.properties.units;
                                        toolText =
                                            s +
                                            `<tr style="line-height: 16px;"><td class="tooltip-data-title style="color: ${
                                                point.point.color
                                            }">
                                                <b>${seriesName}<b>
                                                </td>
                                                <td style="text-align: right">${value}</td>
                                                <td style="padding: 0 5px 0 5px">/</td>
                                                <td style="text-align: right">${
                                                    alternateTooltipValue
                                                        ? point.point.properties.alternateY
                                                        : point.point.properties.units
                                                } units</td>
                                            </tr>
                                            `;
                                    }

                                    if (isPreviousPeriod) {
                                        toolText = s;
                                        comparePeriodTotal = point.y;
                                        comparePeriodUnitTotal = point.point.properties.alternateY;
                                        previousPeridLine = `<tr style="text-align: right; line-height: 16px;">
                                                <td style="padding-right: 15px;color:${
                                                    point.point.color
                                                }">
                                                    <b>Compared to ${point.point.properties.datePeriodDescription?.toUpperCase()}</b>
                                                </td>
                                                <td>${value}</td>
                                                <td style="padding: 0 5px 0 5px">/</td>
                                                <td>${
                                                    alternateTooltipValue
                                                        ? point.point.properties.alternateY
                                                        : point.point.properties.units
                                                } units</td>
                                            </tr>`;
                                    }

                                    if (index === length - 1) {
                                        let percentVoumeInfo =
                                            _this.reusableFunctions.getPercentInfo(
                                                thisPeriodTotal,
                                                comparePeriodTotal,
                                                isInverse
                                            );
                                        if (comparePeriodUnitTotal == null)
                                            comparePeriodUnitTotal = 0;
                                        let percentUnitInfo =
                                            _this.reusableFunctions.getPercentInfo(
                                                thisPeriodUnitTotal,
                                                comparePeriodUnitTotal,
                                                isInverse
                                            );
                                        toolText =
                                            s +
                                            `<tr style="border-top: 1px solid #b3b3b3; line-height: 16px;">
                                                <td><b>Total ${tootipTotalName}</b></td>
                                                <td style="text-align: right;"><b>${_this.reusableFunctions.formatMoney(
                                                    thisPeriodTotal
                                                )}</b></td>
                                                <td style="padding: 0 5px 0 5px">/</td>
                                                <td style="text-align: right;"><b>${thisPeriodUnitTotal.toLocaleString()} units</b></td>
                                            </tr>
                                            ` +
                                            previousPeridLine +
                                            `<tr style="line-height: 16px;">
                                                <td></td>
                                                <td style="text-align: right; color: ${percentVoumeInfo.percentColor}">
                                                    <b><span>
                                                        <i style="padding-right: 5px" class='${percentVoumeInfo.caret}'></i>
                                                        ${percentVoumeInfo.percentValue}
                                                    </b></span>
                                                </td>
                                                <td style="padding: 0 5px 0 5px">/</td>
                                                <td style="text-align: right; color:${percentUnitInfo.percentColor}">
                                                    <b><span><i style="padding-right: 5px" class='${percentUnitInfo.caret}'></i>${percentUnitInfo.percentValue}</span></b>
                                                </td>
                                            </tr>`;
                                    }

                                    return toolText;
                                }, '<tr style="line-height: 18px; font-size: 16px">') +
                                '</table>';
                        } else {
                            let length = this.points.length;
                            let thisPeriodTotal = 0;
                            let thisPeriodUnitTotal = 0;
                            let comparePeriodTotal = 0;
                            let comparePeriodUnitTotal = 0;

                            tooltip =
                                '<table>' +
                                this.points.reduce(function (s, point, index) {
                                    let isPreviousPeriod =
                                        point.series.name.toLowerCase() ===
                                        lineSeriesTitle.toLowerCase();
                                    if (isPreviousPeriod) {
                                        comparePeriodTotal += point.y;
                                        comparePeriodUnitTotal += point.point.properties.units;
                                    } else {
                                        thisPeriodUnitTotal += point.point.properties.units;
                                        thisPeriodTotal += point.y;
                                    }
                                    s +=
                                        '<tr><td style="color:' +
                                        point.point.color +
                                        '; padding-right: 10px;"><b>' +
                                        point.point.properties.datePeriodDescription.toUpperCase() +
                                        '</b></td><td style="text-align: right;">' +
                                        _this.reusableFunctions.formatMoney(point.y) +
                                        '</td><td style="padding: 0 5px 0 5px">/</td>' +
                                        '<td style="text-align: right;">' +
                                        point.point.properties.units.toLocaleString() +
                                        '</td></tr>';

                                    if (index === length - 1) {
                                        let percentVoumeInfo =
                                            _this.reusableFunctions.getPercentInfo(
                                                thisPeriodTotal,
                                                comparePeriodTotal,
                                                isInverse
                                            );
                                        let percentUnitInfo =
                                            _this.reusableFunctions.getPercentInfo(
                                                thisPeriodUnitTotal,
                                                comparePeriodUnitTotal,
                                                isInverse
                                            );

                                        s +=
                                            '<tr style="line-height: 16px;"><td></td><td style="text-align: right; color:' +
                                            percentVoumeInfo.percentColor +
                                            ';"><b><span><i style="padding-right: 5px"class="' +
                                            percentVoumeInfo.caret +
                                            '"></i>' +
                                            percentVoumeInfo.percentValue +
                                            '</b></span></td>' +
                                            '<td style="padding: 0 5px 0 5px">/</td><td style="text-align: right; color:' +
                                            percentUnitInfo.percentColor +
                                            ';"><b><span><i style="padding-right: 5px" class="' +
                                            percentUnitInfo.caret +
                                            '"></i>' +
                                            percentUnitInfo.percentValue +
                                            '</b></span></td></tr>';
                                    }

                                    return s;
                                }, '<tr style="line-height: 18px; font-size: 16px"><td colSpan="4"><b>' +
                                    secondarytitle +
                                    '<b></td></tr>') +
                                '</table>';
                        }
                        return tooltip;
                    },
                } as Highcharts.TooltipOptions,
                lang: {
                    noData: 'There are no data results.',
                },
                noData: {
                    style: {
                        fontWeight: 'bold',
                        fontSize: '15px',
                        color: '#303030',
                    },
                },
            };
        }

        getPurpose(
            urlSegment: string,
            name: string,
            purpose: string,
            lineSeriesTitle: string
        ): any {
            if (urlSegment.includes('purpose')) {
                if (name.toLowerCase() === lineSeriesTitle.toLowerCase()) {
                    return purpose;
                }
                return name;
            } else {
                return purpose;
            }
        }

        getLoanType(
            urlSegment: string,
            name: string,
            loanType: string,
            lineSeriesTitle: string
        ): any {
            if (urlSegment.includes('loan-type') || urlSegment.includes('sold-to-investor')) {
                if (name.toLowerCase() === lineSeriesTitle.toLowerCase()) {
                    return loanType;
                }
                return name;
            } else {
                return loanType;
            }
        }

        public getQueryParams(
            urlSegment: string,
            seriesName,
            period: string,
            compareTo: string,
            periodGroup: string,
            purpose,
            loanType,
            lineSeriesTitle: string
        ) {
            let queryParams: Object;

            if (urlSegment.search('fall-out') > -1) {
                queryParams = {
                    selectedPeriod: period,
                    periodGroup: periodGroup,
                    falloutType:
                        seriesName.toLowerCase() === lineSeriesTitle.toLowerCase()
                            ? 'All'
                            : seriesName,
                    compareTo: compareTo,
                    titleSuffix: seriesName === 'All' ? '' : ': ' + seriesName,
                };
            } else {
                queryParams = {
                    purpose: this.getPurpose(urlSegment, seriesName, purpose, lineSeriesTitle),
                    loanType: this.getLoanType(urlSegment, seriesName, loanType, lineSeriesTitle),
                    compareTo: compareTo,
                    selectedPeriod: period,
                    periodGroup: periodGroup,
                    titleSuffix: seriesName === 'All' ? '' : ': ' + seriesName,
                };
            }

            return queryParams;
        }

        setLineChartSeriesOptions(
            data,
            type: string,
            color: string,
            markerSymbol: string,
            clickablePoints: boolean = false,
            compareTo: string,
            route: string,
            urlSegment,
            name: string,
            purpose: string,
            loanType: string,
            params = undefined
        ) {
            const _this = this;
            return {
                data: data,
                type: type,
                color: color,
                marker: {
                    symbol: markerSymbol,
                },
                name: name,
                legendIndex: 20,
                point: {
                    events: {
                        click: function (e) {
                            if (clickablePoints) {
                                let queryParams: any = _this.getQueryParams(
                                    urlSegment,
                                    this.series.name,
                                    this.properties.period,
                                    compareTo,
                                    'C',
                                    purpose,
                                    loanType,
                                    this.series.name
                                );

                                if (params) {
                                    queryParams = params;
                                    queryParams.periodNumber = e.point.properties.period + 1;
                                }

                                _this.navigationHelpService.navigateTo(
                                    [route, urlSegment],
                                    queryParams,
                                    true
                                );
                            }
                        },
                    },
                },
            } as Highcharts.SeriesOptionsType;
        }
    }
}
